"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.PolylineRegion = void 0;
const { ChangeEventType } = require("../../Interface/IRegionCallbacks");
const { Point2D } = require("../../Core/Point2D");
const Rect_1 = require("../../Core/Rect");
const Region_1 = require("../Region");
const AnchorsElement_1 = require("./AnchorsElement");
const DragElement_1 = require("./DragElement");
const TagsElement_1 = require("./TagsElement");
/**
 * The polyline-type region class.
 */
class PolylineRegion extends Region_1.Region {
    /**
     * Creates new `PolylineRegion` object.
     * @param paper - The `Snap.Paper` object to draw on.
     * @param paperRect - The parent bounding box for created component.
     * @param regionData - The `RegionData` object shared across components. Used also for initial setup.
     * @param callbacks - The external callbacks collection.
     * @param id - The region `id` used to identify regions in `RegionsManager`.
     * @param tagsDescriptor - The descriptor of region tags.
     * @param tagsUpdateOptions - The drawing options for tags.
     */
    constructor(paper, paperRect = null, regionData, callbacks, id, tagsDescriptor, tagsUpdateOptions) {
        super(paper, paperRect, regionData, Object.assign({}, callbacks), id, tagsDescriptor, tagsUpdateOptions);
        if (paperRect !== null) {
            this.paperRects = {
                actual: new Rect_1.Rect(paperRect.width - regionData.width, paperRect.height - regionData.height),
                host: paperRect,
            };
        }

        this.direction = regionData.direction || "none";
        this.arrow1 = null;
        this.arrow2 = null;
        this.buildOn(paper);
        const onChange = this.callbacks.onChange;
        this.callbacks.onChange = (region, regionData, ...args) => {
            this.paperRects.actual.resize(this.paperRects.host.width - regionData.width, this.paperRects.host.height - regionData.height);
            onChange(this, regionData, ...args);
        };
    }
    /**
     * Updates region tags.
     * @param tags - The new tags descriptor object.
     * @param options - The tags drawing options.
     */
    updateTags(tags, options) {
        super.updateTags(tags, options);
        this.tagsNode.updateTags(tags, options);
        this.node.select("title").node.innerHTML = (tags !== null) ? tags.toString() : "";
    }
    /**
     * Converts a hex color code to an RGBA string.
     * @param {string} hex - The hex color code, e.g., "#RRGGBB".
     * @param {number} alpha - The alpha value (opacity) between 0 and 1.
     * @returns {string} The RGBA string, e.g., "rgba(R, G, B, A)".
     */
    hexToRGBA(hex, alpha = 1.0) {
        // Remove the hash (#) if present
        hex = hex.replace(/^#/, '');

        // Parse the red, green, and blue components
        let bigint = parseInt(hex, 16);
        let r = (bigint >> 16) & 255;
        let g = (bigint >> 8) & 255;
        let b = bigint & 255;

        // Construct the RGBA string
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
    /**
     * Resizes the region to specified `width` and `height`.
     * @param width - The new region width.
     * @param height - The new region height.
     */
    resize(width, height) {
        this.paperRects.actual.resize(this.paperRects.host.width - width, this.paperRects.host.height - height);
        super.resize(width, height);
    }
    /**
     * Creates the UI of the region component.
     * @param paper - The `Snap.Paper` element to draw on.
     */
    buildOn(paper) {
        this.node = paper.g();
        this.node.addClass("regionStyle");
        this.node.addClass(this.styleID);
    
        // Original nodes with updated regionData
        this.dragNode = new DragElement_1.DragElement(paper, this.paperRects.actual, this.regionData, this.callbacks);
        this.tagsNode = new TagsElement_1.TagsElement(paper, this.paperRect, this.regionData, this.tags, this.styleID, this.styleSheet, this.tagsUpdateOptions);
        this.anchorNode = new AnchorsElement_1.AnchorsElement(paper, this.paperRect, this.regionData, this.callbacks);
    
        this.toolTip = Snap.parse(`<title>${(this.tags !== null) ? this.tags.toString() : ""}</title>`);
        this.node.append(this.toolTip);
    
        // Add original nodes
        this.node.add(this.dragNode.node);
        this.node.add(this.tagsNode.node);
        this.node.add(this.anchorNode.node);

        const getLineColor = () => {
            return this.tagsNode.tags.allTags[0].color;
        };

        const color = getLineColor();
        const colorRGBA = this.hexToRGBA(color, 0.2);
        const direction = this.regionData.direction || "none";

        // Create and add the first arrow polyline
        this.arrow1 = paper.polyline(0, 0, 0, 0, 0, 0, 0, 0); // Initial coordinates, will be updated
        this.arrow1.attr({
            stroke: color, // Set the stroke color
            strokeWidth: 3, // Set the stroke width
            fill: "none", // Ensure the polyline is not filled
            selected: false,
            x: 0,
            y: 0,
        });
        this.node.add(this.arrow1);
    
        // Add an invisible hitbox for arrow1 as a point
        this.arrow1Hitbox = paper.circle(0, 0, 0);
        this.arrow1Hitbox.attr({
            stroke: "transparent",
            fill: "transparent",
            pointerEvents: "all"
        });
        this.node.add(this.arrow1Hitbox);

        // Create and add the second arrow polyline
        this.arrow2 = paper.polyline(0, 0, 0, 0, 0, 0, 0, 0); // Initial coordinates, will be updated
        this.arrow2.attr({
            stroke: color, // Set the stroke color
            strokeWidth: 3, // Set the stroke width
            fill: "none", // Ensure the polyline is not filled
            selected: false,
            x: 0,
            y: 0
        });
        this.node.add(this.arrow2);

        // Add an invisible hitbox for arrow2
        this.arrow2Hitbox = paper.circle(0, 0, 0);
        this.arrow2Hitbox.attr({
            stroke: "transparent",
            fill: "transparent",
            pointerEvents: "all"
        });
        this.node.add(this.arrow2Hitbox);

        this.lineHitbox = paper.polyline(0, 0, 0, 0, 0, 0, 0, 0); // Same initial coordinates
        this.lineHitbox.attr({
            stroke: "transparent",
            strokeWidth: 5,
        });
        this.node.add(this.lineHitbox);

        // Set arrow colors based on direction
        if (direction === "none") {
            this.arrow1.attr({ stroke: color });
            this.arrow2.attr({ stroke: color });
        } else if (direction === "right") {
            this.arrow1.attr({ stroke: "green" });
            this.arrow2.attr({ stroke: "orange" });
        } else if (direction === "left") {
            this.arrow1.attr({ stroke: "orange" });
            this.arrow2.attr({ stroke: "green" });
        }

        const updateArrowShapes = () => {
            // Original line coordinates
            var x1 = this.regionData.points[0].x;
            var y1 = this.regionData.points[0].y;
            var x2 = this.regionData.points[1].x;
            var y2 = this.regionData.points[1].y;
    
            // Calculate the midpoint of the original line
            var midX = (x1 + x2) / 2;
            var midY = (y1 + y2) / 2;

            // Calculate the length of the original line
            var length = Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
    
            // Determine the length of the arrow legs
            var arrowLegLength = Math.min(Math.max(length / 12, 10), 20);
    
            // Calculate the direction vector of the original line
            var dx = x2 - x1;
            var dy = y2 - y1;
            var mag = Math.sqrt(dx * dx + dy * dy);
            var dirX = dx / mag;
            var dirY = dy / mag;
    
            // Calculate the perpendicular vector
            var perpX = -dirY;
            var perpY = dirX;
    
            // Offset distance for the arrow points
            var offset = 25;
    
            // Angle for the arrow legs (in radians)
            var angle = Math.PI / 6; // 30 degrees
    
            // Calculate the direction vectors for the arrow legs
            var arrowDirX1 = perpX * Math.cos(angle) - perpY * Math.sin(angle);
            var arrowDirY1 = perpY * Math.cos(angle) + perpX * Math.sin(angle);
            var arrowDirX2 = perpX * Math.cos(-angle) - perpY * Math.sin(-angle);
            var arrowDirY2 = perpY * Math.cos(-angle) + perpX * Math.sin(-angle);
    
            // Calculate the starting points of the arrow legs (arrow tips)
            var tipX1 = midX + perpX * offset;
            var tipY1 = midY + perpY * offset;
            var tipX2 = midX - perpX * offset;
            var tipY2 = midY - perpY * offset;
    
            // Calculate the ending points of the arrow legs
            var endX1a = tipX1 - arrowDirX1 * arrowLegLength;
            var endY1a = tipY1 - arrowDirY1 * arrowLegLength;
            var endX1b = tipX1 - arrowDirX2 * arrowLegLength;
            var endY1b = tipY1 - arrowDirY2 * arrowLegLength;
    
            var endX2a = tipX2 + arrowDirX1 * arrowLegLength;
            var endY2a = tipY2 + arrowDirY1 * arrowLegLength;
            var endX2b = tipX2 + arrowDirX2 * arrowLegLength;
            var endY2b = tipY2 + arrowDirY2 * arrowLegLength;
    
            // Calculate the midpoint between the arrow tip and the average of the arrow leg endpoints
            // This will position the hitbox halfway between the tip and the legs
            var arrow1HitboxX = tipX1 - ((arrowDirX1 + arrowDirX2) / 2) * (arrowLegLength / 2);
            var arrow1HitboxY = tipY1 - ((arrowDirY1 + arrowDirY2) / 2) * (arrowLegLength / 2);
            var arrow2HitboxX = tipX2 + ((arrowDirX1 + arrowDirX2) / 2) * (arrowLegLength / 2);
            var arrow2HitboxY = tipY2 + ((arrowDirY1 + arrowDirY2) / 2) * (arrowLegLength / 2);
    
            // Update the coordinates of the arrow shapes
            this.arrow1.attr({ 
                points: `${tipX1},${tipY1} ${endX1a},${endY1a} ${tipX1},${tipY1} ${endX1b},${endY1b}` ,
                x: `${tipX1}`,
                y: `${tipY1}`
            });
            this.arrow1Hitbox.attr({
                cx: arrow1HitboxX,
                cy: arrow1HitboxY,
                r: arrowLegLength
            });
            this.arrow2.attr({ 
                points: `${tipX2},${tipY2} ${endX2a},${endY2a} ${tipX2},${tipY2} ${endX2b},${endY2b}` ,
                x: `${tipX2}`,
                y: `${tipY2}`
            });
            this.arrow2Hitbox.attr({
                cx: arrow2HitboxX,
                cy: arrow2HitboxY,
                r: arrowLegLength
            }); 

            this.lineHitbox.attr({
                points: `${x1},${y1} ${x2},${y2}`
            });
        };
    
        // Update polylines initially
        updateArrowShapes();

        // Update polylines when the region is moved or resized
        const originalRedraw = this.dragNode.redraw.bind(this.dragNode); // Save the original redraw function
        this.dragNode.redraw = () => {
            originalRedraw(); // Call the original redraw logic
            updateArrowShapes(); // Update the arrow polylines
        };
    
        // Add click event listeners to the arrow polylines
        this.arrow1Hitbox.click((e) => {
            if(this.arrow1.selected) {
                this.arrow1.selected = false;
                this.arrow1.attr({ stroke: color });
                this.arrow2.selected = false;
                this.arrow2.attr({ stroke: color });
                this.regionData.direction = "none";
                this.direction = "none";
            } else {
                this.arrow1.selected = true;
                this.arrow2.selected = false;
                this.arrow1.attr({ stroke: this.arrow1.selected ? "green" : color });
                this.arrow2.selected = false;
                this.arrow2.attr({ stroke: "orange" });
                this.regionData.direction = "right";
                this.direction = "right";
            }
            
            this.isDragged = true;
            this.dragOrigin = new Point2D(e.clientX, e.clientY);
            this.callbacks.onManipulationLockRequest(this);
            this.callbacks.onChange(this, this.regionData.copy(), ChangeEventType.MOVEEND, e.ctrlKey);
            
            e.stopPropagation();
        });
        this.arrow2Hitbox.click((e) => {
            if(this.arrow2.selected) {
                this.arrow2.selected = false;
                this.arrow2.attr({ stroke: color });
                this.arrow1.selected = false;
                this.arrow1.attr({ stroke: color });
                this.regionData.direction = "none";
                this.direction = "none";
            } else {
                this.arrow2.selected = true;
                this.arrow1.selected = false;
                this.arrow2.attr({ stroke: this.arrow2.selected ? "green" : color });
                this.arrow1.selected = false;
                this.arrow1.attr({ stroke: "orange" });
                this.regionData.direction = "left";
                this.direction = "left";
            }
            this.isDragged = true;
            this.dragOrigin = new Point2D(e.clientX, e.clientY);
            this.callbacks.onManipulationLockRequest(this);
            this.callbacks.onChange(this, this.regionData.copy(), ChangeEventType.MOVEEND, e.ctrlKey);
            
            e.stopPropagation();
        });

        // Add hover effects
        this.arrow1Hitbox.mouseover(() => {
            this.arrow1.attr({ strokeWidth: 4 });
            this.arrow1Hitbox.attr({ stroke: colorRGBA, fill: colorRGBA });
        });
        this.arrow1Hitbox.mouseout(() => {
            this.arrow1.attr({ strokeWidth: 3 });
            this.arrow1Hitbox.attr({ stroke: "transparent", fill: "transparent" });
        });
        this.arrow2Hitbox.mouseover(() => {
            this.arrow2.attr({ strokeWidth: 4 });
            this.arrow2Hitbox.attr({ stroke: colorRGBA, fill: colorRGBA });
        });
        this.arrow2Hitbox.mouseout(() => {
            this.arrow2.attr({ strokeWidth: 3 });
            this.arrow2Hitbox.attr({ stroke: "transparent", fill: "transparent" });
        });

        // Add event listeners to lineHitbox for drag behavior
        this.lineHitbox.mousedown((e) => {
            // Start the drag operation
            this.dragNode.isDragged = true;
            this.dragNode.dragOrigin = new Point2D(e.clientX, e.clientY);
            this.callbacks.onManipulationLockRequest(this);
            this.callbacks.onChange(this, this.regionData.copy(), ChangeEventType.MOVEBEGIN, e.ctrlKey);
            
            // Capture pointer if applicable
            if (e.pointerId) {
                this.lineHitbox.node.setPointerCapture(e.pointerId);
            }
            
            // Define handlers
            const handleMove = (moveEvent) => {
                if (!this.dragNode.isDragged) return;
                
                // Calculate deltas
                let dx = moveEvent.clientX - this.dragNode.dragOrigin.x;
                let dy = moveEvent.clientY - this.dragNode.dragOrigin.y;
                
                // Update origin point
                this.dragNode.dragOrigin = new Point2D(moveEvent.clientX, moveEvent.clientY);
                
                // Move the region
                const rd = this.regionData.copy();
                rd.move(new Point2D(this.dragNode.x + dx, this.dragNode.y + dy)
                    .boundToRect(this.dragNode.paperRect));
                this.callbacks.onChange(this, rd, ChangeEventType.MOVING);
            };
            
            const handleUp = (upEvent) => {
                if (!this.dragNode.isDragged) return;
                
                // End the drag operation
                const multiselection = upEvent.ctrlKey;
                this.callbacks.onChange(this, this.regionData.copy(), ChangeEventType.MOVEEND, multiselection);
                this.dragNode.isDragged = false;
                this.dragNode.dragOrigin = null;
                this.callbacks.onManipulationLockRelease(this);
                this.callbacks.onChange(this, this.regionData.copy(), ChangeEventType.SELECTIONTOGGLE, multiselection);
                
                // Clean up event listeners
                document.removeEventListener('mousemove', handleMove);
                document.removeEventListener('mouseup', handleUp);
            };
            
            // Add document listeners
            document.addEventListener('mousemove', handleMove);
            document.addEventListener('mouseup', handleUp);
            
            e.stopPropagation();
        });

            // Push to UI array
            this.UI.push(this.tagsNode, this.dragNode, this.anchorNode);
        }
    
    
}
exports.PolylineRegion = PolylineRegion;
//# sourceMappingURL=PolylineRegion.js.map